import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  deleteMedia,
  mediaUrl,
  subscribeToMedia,
  updateMediaMeta,
  uploadMedia,
  type MediaDoc,
} from '../services/media';

/**
 * The Media Library.
 *
 * Every catalogue image on the site comes from here — nothing is hotlinked and
 * nothing ships with the code. Bulk drag-and-drop matters because populating a
 * 114-category catalogue one file at a time is not a realistic afternoon.
 */
export function MediaLibrary() {
  const [items, setItems] = useState<MediaDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploads, setUploads] = useState<Record<string, number>>({});
  const [errors, setErrors] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<MediaDoc | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => subscribeToMedia((next) => {
    setItems(next);
    setLoading(false);
  }), []);

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (!list.length) return;
    setErrors([]);

    // Sequential rather than parallel: twenty simultaneous 20 MB uploads
    // saturate a typical Indian office connection and every one of them
    // slows down. One at a time finishes sooner in practice.
    for (const file of list) {
      const key = `${file.name}-${file.size}`;
      try {
        setUploads((u) => ({ ...u, [key]: 0 }));
        await uploadMedia(file, {}, (p) => setUploads((u) => ({ ...u, [key]: p })));
      } catch (err) {
        setErrors((e) => [...e, err instanceof Error ? err.message : `${file.name} failed to upload.`]);
      } finally {
        setUploads((u) => {
          const next = { ...u };
          delete next[key];
          return next;
        });
      }
    }
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (i) =>
        i.alt?.toLowerCase().includes(q) ||
        i.originalFilename?.toLowerCase().includes(q) ||
        i.tags?.some((t) => t.toLowerCase().includes(q)),
    );
  }, [items, search]);

  const activeUploads = Object.entries(uploads);

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="h1" sx={{ flexGrow: 1 }}>
          Media library
        </Typography>
        <TextField
          size="small"
          placeholder="Search name, alt or tag"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 260 }}
        />
        <Button variant="contained" onClick={() => inputRef.current?.click()}>
          Upload images
        </Button>
        <input
          ref={inputRef}
          type="file"
          hidden
          multiple
          accept="image/*"
          onChange={(e) => {
            if (e.target.files) void handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </Stack>

      <Paper
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void handleFiles(e.dataTransfer.files);
        }}
        sx={{
          p: 4,
          textAlign: 'center',
          borderStyle: 'dashed',
          borderWidth: 2,
          borderColor: dragging ? 'primary.main' : 'divider',
          bgcolor: dragging ? 'action.hover' : 'transparent',
          transition: 'background-color 120ms, border-color 120ms',
        }}
      >
        <Typography color="text.secondary">
          Drop images here — JPEG, PNG, WebP, AVIF or HEIC, up to 25 MB each.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Thumbnail, medium and large WebP versions are generated automatically.
        </Typography>
      </Paper>

      {activeUploads.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Uploading {activeUploads.length} file{activeUploads.length === 1 ? '' : 's'}
          </Typography>
          {activeUploads.map(([key, progress]) => (
            <Box key={key} sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {key.replace(/-\d+$/, '')}
              </Typography>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          ))}
        </Paper>
      )}

      {errors.map((msg, i) => (
        <Alert key={i} severity="error" onClose={() => setErrors((e) => e.filter((_, j) => j !== i))}>
          {msg}
        </Alert>
      ))}

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {items.length === 0 ? 'No images yet.' : 'Nothing matches that search.'}
          </Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
            gap: 2,
          }}
        >
          {filtered.map((item) => (
            <MediaTile key={item.id} item={item} onEdit={() => setEditing(item)} />
          ))}
        </Box>
      )}

      <EditDialog item={editing} onClose={() => setEditing(null)} />
    </Stack>
  );
}

function MediaTile({ item, onEdit }: { item: MediaDoc; onEdit: () => void }) {
  const url = mediaUrl(item, 'thumb');

  return (
    <Paper sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          aspectRatio: '4 / 3',
          bgcolor: 'action.hover',
          display: 'grid',
          placeItems: 'center',
          cursor: item.status === 'READY' ? 'pointer' : 'default',
        }}
        onClick={() => item.status === 'READY' && onEdit()}
      >
        {item.status === 'PROCESSING' && <CircularProgress size={22} />}
        {item.status === 'FAILED' && (
          <Tooltip title={item.error ?? 'Processing failed'}>
            <ErrorOutlineIcon color="error" />
          </Tooltip>
        )}
        {item.status === 'READY' && url && (
          <Box
            component="img"
            src={url}
            alt={item.alt}
            loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
      </Box>

      <Stack sx={{ p: 1, gap: 0.5, flexGrow: 1 }}>
        <Typography variant="caption" noWrap title={item.alt || item.originalFilename}>
          {item.alt || item.originalFilename}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {item.tags?.slice(0, 3).map((tag) => (
            <Chip key={tag} label={tag} size="small" variant="outlined" />
          ))}
        </Stack>
        <Stack direction="row" spacing={0.5} sx={{ mt: 'auto', pt: 0.5 }}>
          <Tooltip title="Copy image id">
            <IconButton size="small" onClick={() => navigator.clipboard.writeText(item.id)}>
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove from library">
            <IconButton size="small" onClick={() => void deleteMedia(item.id)}>
              <DeleteOutlineIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          {item.width ? (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', alignSelf: 'center' }}>
              {item.width}&times;{item.height}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </Paper>
  );
}

function EditDialog({ item, onClose }: { item: MediaDoc | null; onClose: () => void }) {
  const [alt, setAlt] = useState('');
  const [tags, setTags] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAlt(item?.alt ?? '');
    setTags((item?.tags ?? []).join(', '));
  }, [item]);

  if (!item) return null;

  const save = async () => {
    setSaving(true);
    try {
      await updateMediaMeta(item.id, {
        alt: alt.trim(),
        tags: tags
          .split(',')
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Image details</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Box
            component="img"
            src={mediaUrl(item, 'md')}
            alt={item.alt}
            sx={{ width: '100%', borderRadius: 1, bgcolor: 'action.hover' }}
          />
          <TextField
            label="Alt text"
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
            fullWidth
            helperText="Describes the image for screen readers and search engines."
          />
          <TextField
            label="Tags"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            fullWidth
            helperText="Comma separated, e.g. visiting-cards, mockup, lifestyle"
          />
          <Typography variant="caption" color="text.secondary">
            {item.originalFilename} &middot; {item.width}&times;{item.height} &middot;{' '}
            {item.bytes ? `${(item.bytes / 1024).toFixed(0)} KB` : ''} &middot; id {item.id}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
